import type { ReactNode } from "react";
import { Lock } from "lucide-react";
import { useEmployerAccess } from "@/hooks/useEmployerAccess";
import { EmployerAccessCTA } from "./EmployerAccessCTA";

type Props = {
  children: ReactNode;
  /** What is locked, e.g. "your full program list". */
  feature?: string;
  className?: string;
  /** Small inline version for tight spaces (charts, buttons). */
  compact?: boolean;
};

/**
 * Wraps the locked part of a tool. Members with access see children;
 * everyone else sees a short card asking them to get access through their employer.
 * Put the free preview OUTSIDE this gate.
 */
export function EmployerAccessGate({ children, feature = "the full results", className, compact = false }: Props) {
  const { hasAccess, loading } = useEmployerAccess();
  if (loading) return null;
  if (hasAccess) return <>{children}</>;
  if (compact) {
    return (
      <div className={`flex h-full min-h-[120px] w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/40 p-4 text-center ${className ?? ""}`}>
        <Lock className="h-4 w-4 text-primary" aria-hidden />
        <p className="text-xs text-muted-foreground">Unlock {feature} through your employer.</p>
        <a href="/request-access" className="text-xs font-semibold text-primary underline">Get access through your employer</a>
      </div>
    );
  }
  return (
    <section className={`rounded-2xl border border-border bg-card p-6 text-center shadow-sm ${className ?? ""}`}>
      <Lock className="mx-auto mb-3 h-6 w-6 text-primary" aria-hidden />
      <h3 className="text-lg font-semibold text-foreground">Unlock {feature}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Home Approach is offered as a benefit through employers. Tell us where you work and we'll help bring it to your team.
      </p>
      <div className="mt-4 flex flex-col items-center gap-2">
        <EmployerAccessCTA />
        <a href="/auth?signin=true" className="text-sm text-primary underline">Already a member? Sign in</a>
      </div>
    </section>
  );
}

export default EmployerAccessGate;
